import { supabase } from './client';

/**
 * Parse OAuth callback from URL and handle session establishment
 * This is called when the app receives the OAuth redirect with auth parameters in the URL
 */
export async function handleOAuthCallback(redirectUrl?: string) {
  try {
    console.log('OAuth Callback Handler: Initializing auth session from URL...');
    
    // The redirectUrl can be used to process any URL parameters if needed
    // The Supabase client should automatically detect session from URL hash
    
    // Explicitly check if a session was established from the URL
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('OAuth Callback Error - Could not retrieve session:', error);
      throw error;
    }
    
    if (session?.user) {
      console.log('OAuth Callback Handler: Session established for user:', session.user.id);
      
      // Clean the access_token and other OAuth params from the URL hash
      // so they don't linger in the address bar or get leaked
      if (window.location.hash.includes('access_token')) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        console.log('OAuth Callback Handler: Cleaned OAuth tokens from URL');
      }
      
      return {
        success: true,
        session,
        user: session.user
      };
    } else {
      console.log('OAuth Callback Handler: No session found after OAuth redirect');
      return {
        success: false,
        session: null,
        user: null,
        message: 'OAuth redirect received but session not established'
      };
    }
  } catch (error) {
    console.error('OAuth Callback Handler Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      session: null,
      user: null
    };
  }
}

/**
 * Listen for OAuth completion and handle the result
 * This should be called during app initialization or on the login page
 */
export function onOAuthStateChange(
  callback: (state: 'loading' | 'success' | 'error', data?: any) => void
) {
  callback('loading', null);
  
  const { data: { subscription } } = supabase.auth.onAuthStateChange(
    async (event, session) => {
      console.log('Auth State Change:', event);
      
      if (event === 'SIGNED_IN' && session?.user) {
        console.log('User signed in via OAuth:', session.user.id);
        callback('success', { session, user: session.user });
      } else if (event === 'SIGNED_OUT') {
        console.log('User signed out');
        callback('error', { message: 'Signed out' });
      } else if (event === 'USER_UPDATED' && session?.user) {
        console.log('User updated');
        callback('success', { session, user: session.user });
      }
    }
  );
  
  // Return unsubscribe function
  return () => subscription?.unsubscribe();
}

/**
 * Retry getting session with backoff
 * Useful for handling race conditions during OAuth redirect
 */
export async function getSessionWithRetry(
  maxRetries: number = 3,
  delayMs: number = 100
): Promise<any> {
  let lastError: any = null;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        lastError = error;
        console.warn(`Attempt ${i + 1}/${maxRetries} - Session error:`, error);
      } else if (session?.user) {
        console.log(`Session found on attempt ${i + 1}`);
        return session;
      } else {
        console.log(`Attempt ${i + 1}/${maxRetries} - No session yet`);
      }
      
      // Wait before retrying (except on last iteration)
      if (i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delayMs * Math.pow(2, i)));
      }
    } catch (error) {
      lastError = error;
      console.warn(`Attempt ${i + 1}/${maxRetries} - Exception:`, error);
    }
  }
  
  console.error('Failed to get session after retries:', lastError);
  return null;
}
